import { useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import ScreenBanner from "../../../../../components/ScreenBanner";
import PermissionGuard from "../../../../../components/PermissionGuard";
import { Box, Button, Chip, Container, Grid, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import LoadingApiQuery from "../../../../../components/LoadingApiQuery";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { IDataResumoOperacoes, IDataTotaisResumoOperacoes } from "../../../../../types/api/comercial/resumo-operacoes";
import { formatarValorMonetario } from "../../../../../helpers/masks";
import DisplayNotFoundInfo from "../../../../../components/DisplayNotFindInfo";
import { TLoja } from "../../../../../components/SelectsSimpleApiQuery/SelectLojaComPesquisa";
import moment, { Moment } from "moment";
import { IDataFormasRecebimento } from "../../../../../types/api/financeiro/pesquisar-formas-recebimento";
import FiltroModal from "./FiltroModal";
import ChipWithTooltip from "../../../../../components/ChipWithTooltip";
import FilterListIcon from "@mui/icons-material/FilterList";
import { AuthContext } from "../../../../../contexts/AuthContext";
import BtnExportarDados from "../../../../../components/BtnExportarDados";

export default function ResumoOperacoes() {

  const containerRef = useRef<HTMLDivElement>(null);
  const { userPerfilComLoja } = useContext(AuthContext);

  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [filtros, setFiltros] = useState<TFiltrosResumoState>(
    userPerfilComLoja && userPerfilComLoja.loja ? {
      ...defaultFiltros,
      loja: userPerfilComLoja.loja
    } : defaultFiltros
  );

  const { data, isLoading: isLoadingResumos } = useQuery({
    queryKey: ["resumo-operacoes", filtros],
    queryFn: () =>
      axiosClient.get("comercial/pesquisar-resumo-operacoes", {
        params: {
          page: filtros.pagina + 1,
          limit: filtros.porPagina,
          loja_id: filtros.loja?.id,
          data_inicio: moment(filtros.data_inicio).format("YYYY-MM-DD"),
          data_fim: moment(filtros.data_fim).format("YYYY-MM-DD"),
          formas_recebimento: filtros.forma_recebimento?.map((item) => item.id) || [],
        },
      }),
    cacheTime: 0,
    enabled: !!filtros.loja
  });

  const { data: dataTotaisResumos, isLoading: isLoadingTotaisResumos } = useQuery({
    queryKey: ["totais-resumo-operacoes", filtros],
    queryFn: () =>
      axiosClient.get("comercial/pesquisar-totais-resumo-operacoes", {
        params: {
          page: filtros.pagina + 1,
          limit: filtros.porPagina,
          loja_id: filtros.loja?.id,
          data_inicio: moment(filtros.data_inicio).format("YYYY-MM-DD"),
          data_fim: moment(filtros.data_fim).format("YYYY-MM-DD"),
          formas_recebimento: filtros.forma_recebimento?.map((item) => item.id) || [],
        },
      }),
    cacheTime: 0,
    enabled: !!filtros.loja
  });

  const dataResumos: IDataResumoOperacoes | null = useMemo(() => {
    if (!data) return null;

    return data.data
  }, [data]);

  const dataTotais: IDataTotaisResumoOperacoes | null = useMemo(() => {
    if (!dataTotaisResumos) return null;

    return dataTotaisResumos.data
  }, [dataTotaisResumos]);

  const handleChangePage = useCallback(
    (_: React.MouseEvent<HTMLButtonElement> | null, novaPag: number) => {
      setFiltros((prevState) => ({
        ...prevState,
        pagina: novaPag,
      }));
    },
    []
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFiltros((prevState) => ({
        ...prevState,
        porPagina: parseInt(event.target.value, 10),
        pagina: 0,
      }));
    },
    []
  );

  return (
    <>
      <ScreenBanner title="Comercial" />
      <PermissionGuard permissionId="comercial.resumo-operacoes">
        <Container sx={{ mt: 3 }}>
          <Typography textAlign="center" variant="h5" mb={2}>Resumo de operações</Typography>

          <Grid container spacing={2}>
            <Grid size={{xs: 12, md: 3}}>
              <Box component={Paper} p={2}>
                {isLoadingTotaisResumos && <LoadingApiQuery />}

                {!isLoadingTotaisResumos && (
                  <>
                    <Typography variant="h6">Valores</Typography>
                    <Grid
                      container
                      spacing={1}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-end"
                      mt={1}
                    >
                      <Grid size={{xs: 12, md: 6}} display="flex" flexDirection="column" alignItems="flex-start">
                        <Typography variant="body2">Quantidade</Typography>
                        <Typography variant="subtitle1"><b>{dataTotais?.total_registros ?? 0}</b></Typography>
                      </Grid>
                      <Grid size={{xs: 12, md: 6}} display="flex" flexDirection="column" alignItems="flex-end">
                        <Typography variant="body2">Valor total</Typography>
                        <Typography variant="subtitle1"><b>{formatarValorMonetario(dataTotais?.total_geral ?? 0)}</b></Typography>
                      </Grid>

                      <Grid size={{xs: 12}}>
                        <Box sx={{ mt: 2, px: 1 }}>
                          {Object.entries(dataTotais?.totais || {}).map(([forma, valor]) => (
                            <Box
                              key={forma}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 0.8,
                                position: 'relative'
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.primary',
                                  textTransform: 'capitalize',
                                  fontSize: '0.875rem',
                                  flex: 1,
                                  pr: 2
                                }}
                              >
                                {forma.replace(/_+/g, ' ')}
                              </Typography>

                              <Box
                                sx={{
                                  flex: 1,
                                  height: '1px',
                                  background: 'repeating-linear-gradient(to right, #666 0, #666 3px, transparent 3px, transparent 8px)',
                                  mx: 1,
                                  opacity: 0.4
                                }}
                              />

                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                sx={{
                                  color: 'text.primary',
                                  fontSize: '0.875rem',
                                  minWidth: 'fit-content'
                                }}
                              >
                                {formatarValorMonetario(valor ?? 0)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Box>
            </Grid>
            <Grid size={{xs: 12, md: 9}} ref={containerRef} >

              <Box component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Stack
                  p={3}
                  direction={'row'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                >
                  <Typography variant="h6">Resumo</Typography>

                  <Stack direction="row" spacing={1}>
                    <BtnExportarDados
                      pathRequisicao="comercial/pesquisar-resumo-operacoes"
                      fileName="resumo-operacoes"
                      exportTitle="Resumo de operações"
                      exportInfo={[
                        { label: "Data de geração", value: moment().format('DD/MM/YYYY HH:mm') },
                        { label: "Loja", value: filtros.loja ? `${filtros.loja.id} - ${filtros.loja.nome}` : "" },
                        { label: "Período", value: `${moment(filtros.data_inicio).format('DD/MM/YYYY')} até ${moment(filtros.data_fim).format('DD/MM/YYYY')}` }
                      ]}
                      formatos={{
                        "csv": "CSV",
                        "pdf": "PDF"
                      }}
                      cabecalhoLabels={[
                        { queryKey: "tipo_publico", label: "Tipo" },
                        { queryKey: "codigo_publico", label: "Cód." },
                        { queryKey: "nome_pessoa", label: "Cliente" },
                        { queryKey: "tipo", label: "Tipo" },
                        { queryKey: "numero", label: "Número" },
                        { queryKey: "forma_recebimento", label: "Forma recebimento" },
                        { queryKey: "data_emissao", label: "Emissão", tipo: "data" },
                        { queryKey: "valor", label: "Valor", tipo: "monetario" }
                      ]}
                      queryParams={{
                        page: filtros.pagina + 1,
                        limit: filtros.porPagina,
                        loja_id: filtros.loja?.id,
                        data_inicio: moment(filtros.data_inicio).format("YYYY-MM-DD"),
                        data_fim: moment(filtros.data_fim).format("YYYY-MM-DD"),
                        formas_recebimento: filtros.forma_recebimento?.map((item) => item.id) || [],
                      }}
                      possuiQuery={true}
                      disabled={!filtros.loja}
                      tipoExportacao={["csv", "pdf"]}
                    />

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FilterListIcon />}
                      onClick={() => setOpenFilterModal(true)}
                    >
                      Filtros
                    </Button>
                  </Stack>
                </Stack>

                <Box sx={{ px: 3 }}>
                  <Stack spacing={3} direction="row" justifyContent="flex-start">
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="text.secondary"
                        sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        Loja
                      </Typography>
                      <Chip
                        variant="outlined"
                        color={filtros.loja ? "primary" : "default"}
                        label={filtros.loja ? `${filtros.loja.id} - ${filtros.loja.nome}` : 'Nenhuma loja selecionada'}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 'medium',
                          '& .MuiChip-label': { px: 2 }
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="text.secondary"
                        sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        Período
                      </Typography>
                      <Chip
                        variant="outlined"
                        color="primary"
                        label={`${moment(filtros.data_inicio).format('DD/MM/YYYY')} até ${moment(filtros.data_fim).format('DD/MM/YYYY')}`}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 'medium',
                          '& .MuiChip-label': { px: 2 }
                        }}
                      />
                    </Box>

                    {filtros.forma_recebimento && filtros.forma_recebimento?.length > 0 && (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          color="text.secondary"
                          sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          Formas de Recebimento
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Chip
                            variant="outlined"
                            color="primary"
                            label={`${filtros.forma_recebimento[0].id} - ${filtros.forma_recebimento[0].nome}`}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 'medium',
                              '& .MuiChip-label': { px: 2 }
                            }}
                          />
                          {filtros.forma_recebimento.length > 1 && (
                            <ChipWithTooltip
                              extraLabels={filtros.forma_recebimento.slice(1).map((forma) => `${forma.id} - ${forma.nome}`)}
                              label={`+${filtros.forma_recebimento.length - 1} mais`}
                              quebraLinha
                            />
                          )}
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </Box>

                {isLoadingResumos && <LoadingApiQuery extraParams={{ height: 200, alignItems: 'center' }} />}

                {
                  !isLoadingResumos && !dataResumos && !filtros.loja &&
                  <DisplayNotFoundInfo
                    label="Selecione uma loja para filtrar os resumos"
                  />
                }

                {!isLoadingResumos && dataResumos &&
                  <>
                    {
                      dataResumos.data.length === 0 &&
                      <DisplayNotFoundInfo
                        label="Nenhum resumo encontrado"
                      />
                    }



                    {
                      dataResumos.data.length > 0 &&
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><b>Cliente</b></TableCell>
                              <TableCell><b>Tipo</b></TableCell>
                              <TableCell><b>Número</b></TableCell>
                              <TableCell><b>Forma recebimento</b></TableCell>
                              <TableCell><b>Emissão</b></TableCell>
                              <TableCell align="right"><b>Valor</b></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dataResumos.data.map((resumo) => (
                              <TableRow key={resumo.numero}>
                                <TableCell>{resumo.tipo_publico}{resumo.codigo_publico} - {resumo.nome_pessoa}</TableCell>
                                <TableCell>{resumo.tipo}</TableCell>
                                <TableCell>{resumo.numero}</TableCell>
                                <TableCell>{resumo.forma_recebimento}</TableCell>
                                <TableCell>{moment(resumo.data_emissao).format('DD/MM/YYYY')}</TableCell>
                                <TableCell align="right">{formatarValorMonetario(resumo.valor)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    }

                    <TablePagination
                      component="div"
                      count={dataResumos?.total}
                      page={filtros.pagina}
                      onPageChange={handleChangePage}
                      rowsPerPage={filtros.porPagina}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      labelRowsPerPage="Linhas por página"
                    />
                  </>
                }
              </Box>
            </Grid>
          </Grid>
        </Container>
      </PermissionGuard>
      {
        openFilterModal &&
        <FiltroModal
          open={openFilterModal}
          onClose={() => setOpenFilterModal(false)}
          onClearFilters={() => setFiltros(defaultFiltros)}
          onFilterSubmit={(newFilters) => { setFiltros(newFilters) }}
          filters={filtros}
        />
      }
    </>
  )
}

export type TFiltrosResumoState = {
  pagina: number;
  porPagina: number;
  loja: TLoja | null;
  data_inicio: Moment;
  data_fim: Moment;
  forma_recebimento: IDataFormasRecebimento[] | null;
}

const defaultFiltros = {
  pagina: 0,
  porPagina: 10,
  loja: null,
  data_inicio: moment().startOf('day'),
  data_fim: moment().endOf('day'),
  forma_recebimento: null
}
